import React, { useState } from 'react';
import ErrorDisplay, {
  FormErrorDisplay,
  LoadingErrorDisplay,
  PageErrorDisplay,
} from '../components/common/ErrorDisplay';
import {
  formatErrorMessage,
  formatAuthError,
  isRetryableError,
} from '../utils/errorFormatting';
import {
  Card,
  Button,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '../components/ui';
import type {
  ApiError,
  FirebaseAuthError,
  NetworkError,
  AppError,
} from '../types/api';

/**
 * Demo page showcasing the enhanced error handling system
 * This page demonstrates various error types and display patterns
 */
const ErrorDemoPage: React.FC = () => {
  const [currentError, setCurrentError] = useState<
    Error | ApiError | FirebaseAuthError | NetworkError | AppError | null
  >(null);
  const [retryCount, setRetryCount] = useState(0);

  // Helper function to simulate different error types
  const simulateError = (errorType: string) => {
    switch (errorType) {
      case 'network': {
        const networkError: NetworkError = {
          name: 'NetworkError',
          message: 'Network connection failed',
        };
        setCurrentError(networkError);
        break;
      }

      case 'auth_expired': {
        const authError: ApiError = new Error('Session expired');
        authError.status = 401;
        setCurrentError(authError);
        break;
      }

      case 'forbidden': {
        const forbiddenError: ApiError = new Error('Access denied');
        forbiddenError.status = 403;
        setCurrentError(forbiddenError);
        break;
      }

      case 'server_error': {
        const serverError: ApiError = new Error('Internal server error');
        serverError.status = 500;
        setCurrentError(serverError);
        break;
      }

      case 'validation': {
        const validationError: ApiError = {
          name: 'ValidationError',
          message: 'Invalid input provided',
          status: 422,
        };
        setCurrentError(validationError);
        break;
      }

      case 'firebase_auth': {
        const firebaseError: FirebaseAuthError = new Error(
          'Invalid credentials'
        ) as FirebaseAuthError;
        firebaseError.code = 'auth/wrong-password';
        setCurrentError(firebaseError);
        break;
      }

      case 'case_not_found': {
        const notFoundError: ApiError = {
          name: 'NotFoundError',
          message: 'Business case not found',
          status: 404,
        };
        setCurrentError(notFoundError);
        break;
      }

      default:
        setCurrentError(new Error('Unknown error occurred'));
    }
  };

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    // Simulate clearing error after retry
    setTimeout(() => {
      setCurrentError(null);
    }, 1000);
  };

  const errorButtons = [
    { type: 'network', label: 'Network Error', Icon: ExclamationCircleIcon },
    {
      type: 'auth_expired',
      label: 'Auth Expired',
      Icon: ExclamationTriangleIcon,
    },
    { type: 'forbidden', label: 'Access Denied', Icon: ExclamationCircleIcon },
    {
      type: 'server_error',
      label: 'Server Error',
      Icon: ExclamationCircleIcon,
    },
    {
      type: 'validation',
      label: 'Validation Error',
      Icon: ExclamationTriangleIcon,
    },
    {
      type: 'firebase_auth',
      label: 'Firebase Auth Error',
      Icon: ExclamationCircleIcon,
    },
    {
      type: 'case_not_found',
      label: 'Case Not Found',
      Icon: InformationCircleIcon,
    },
    { type: 'unknown', label: 'Unknown Error', Icon: ExclamationCircleIcon },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-center text-secondary-900 mb-2">
        Enhanced Error Handling Demo
      </h1>

      <p className="text-center text-secondary-600 mb-8">
        This page demonstrates the enhanced error handling system with
        user-friendly messages, actionable advice, and consistent display
        patterns.
      </p>

      {/* Error Generation Section */}
      <Card className="mb-6" variant="elevated">
        <h2 className="text-xl font-semibold text-secondary-900 mb-2">
          Error Generation
        </h2>
        <p className="text-sm text-secondary-600 mb-4">
          Click any button below to simulate different types of errors and see
          how they're displayed with user-friendly messages and actionable
          advice.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {errorButtons.map(({ type, label, Icon }) => (
            <Button
              key={type}
              variant="outline"
              size="sm"
              onClick={() => simulateError(type)}
              leftIcon={<Icon className="w-4 h-4" />}
            >
              {label}
            </Button>
          ))}
        </div>

        <Button
          variant="primary"
          onClick={() => setCurrentError(null)}
          disabled={!currentError}
          className="bg-success-600 hover:bg-success-700"
        >
          Clear Error
        </Button>

        {retryCount > 0 && (
          <p className="text-sm text-secondary-500 mt-2">
            Retry attempts: {retryCount}
          </p>
        )}
      </Card>

      {/* Error Display Section */}
      {currentError && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-secondary-900 mb-4">
            Current Error Display
          </h2>

          {/* Basic Error Display */}
          <Card className="mb-4">
            <h3 className="text-lg font-semibold text-secondary-900 mb-3">
              Basic Error Display
            </h3>
            <ErrorDisplay
              error={currentError}
              showRetry={isRetryableError(currentError)}
              onRetry={handleRetry}
              showClose={true}
              onClose={() => setCurrentError(null)}
            />
          </Card>

          {/* Context-Specific Displays */}
          <div className="space-y-4">
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-3">
                Form Error Display (Compact)
              </h3>
              <FormErrorDisplay
                error={currentError}
                onClose={() => setCurrentError(null)}
              />
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-3">
                Loading Error Display (With Retry)
              </h3>
              <LoadingErrorDisplay
                error={currentError}
                context="load_cases"
                onRetry={handleRetry}
              />
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-3">
                Page Error Display
              </h3>
              <PageErrorDisplay
                error={currentError}
                context="save_data"
                onRetry={handleRetry}
              />
            </Card>
          </div>
        </div>
      )}

      {/* Error Information Section */}
      <Card className="mb-6" variant="elevated">
        <h2 className="text-xl font-semibold text-secondary-900 mb-4">
          Error Information
        </h2>

        {currentError ? (
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-sm mb-1">Original Error:</p>
              <pre className="text-xs font-mono bg-secondary-100 p-3 rounded overflow-x-auto">
                {JSON.stringify(
                  {
                    name: currentError.name,
                    message: currentError.message,
                    status:
                      'status' in currentError
                        ? currentError.status
                        : undefined,
                    code:
                      'code' in currentError ? currentError.code : undefined,
                  },
                  null,
                  2
                )}
              </pre>
            </div>

            <div>
              <p className="font-semibold text-sm mb-1">Formatted Error:</p>
              <pre className="text-xs font-mono bg-secondary-100 p-3 rounded overflow-x-auto">
                {JSON.stringify(formatErrorMessage(currentError), null, 2)}
              </pre>
            </div>

            {'code' in currentError &&
              typeof currentError.code === 'string' &&
              currentError.code.startsWith('auth/') && (
                <div>
                  <p className="font-semibold text-sm mb-1">
                    Firebase Auth Formatted:
                  </p>
                  <pre className="text-xs font-mono bg-secondary-100 p-3 rounded overflow-x-auto">
                    {JSON.stringify(formatAuthError(currentError), null, 2)}
                  </pre>
                </div>
              )}

            <div>
              <p className="font-semibold text-sm">Is Retryable:</p>
              <p className="text-sm">
                {isRetryableError(currentError) ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-secondary-500">
            No error currently active. Generate an error above to see details.
          </p>
        )}
      </Card>

      {/* Error Handling Features */}
      <Card variant="elevated">
        <h2 className="text-xl font-semibold text-secondary-900 mb-4">
          Enhanced Error Handling Features
        </h2>

        <div className="space-y-4">
          {[
            {
              icon: '✅',
              title: 'User-Friendly Messages',
              desc: 'Technical error messages are automatically converted to user-friendly language that non-technical users can understand.',
            },
            {
              icon: '🎯',
              title: 'Actionable Advice',
              desc: 'Errors include specific guidance on what users can do next, such as checking their internet connection or contacting support.',
            },
            {
              icon: '🔄',
              title: 'Smart Retry Logic',
              desc: 'System automatically determines which errors are worth retrying and shows retry buttons only when appropriate.',
            },
            {
              icon: '🎨',
              title: 'Consistent Display',
              desc: 'All errors use the same Alert components with consistent styling, icons, and interaction patterns throughout the application.',
            },
            {
              icon: '🛡️',
              title: 'Error Boundaries',
              desc: 'React Error Boundaries catch unexpected JavaScript errors and display graceful fallback UI instead of crashing the application.',
            },
            {
              icon: '📱',
              title: 'Context-Aware Messages',
              desc: 'Error messages are customized based on context (login, form submission, data loading, etc.) for more specific and helpful guidance.',
            },
          ].map((feature, index) => (
            <div
              key={index}
              className={index > 0 ? 'border-t border-secondary-200 pt-4' : ''}
            >
              <h3 className="font-semibold text-secondary-900 mb-1">
                {feature.icon} {feature.title}
              </h3>
              <p className="text-sm text-secondary-600">{feature.desc}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ErrorDemoPage;
